/** Shared types for FrugalNick finance calculators. */

export type ProvinceCode = 'ON' | 'BC' | 'AB';

export type TaxYear = 2025 | 2026;

export type CompoundingFrequency = 'annually' | 'quarterly' | 'monthly' | 'daily';

export interface TaxBracket {
  upTo: number;
  rate: number;
}

export interface ProvinceInfo {
  name: string;
  code: ProvinceCode;
}

export interface TfsaInput {
  year: number;
  age: number;
  priorContributions: number;
  canadianResident?: boolean;
}

export interface TfsaYearlyBreakdown {
  year: number;
  annualLimit: number;
  roomAdded: number;
  cumulativeRoom: number;
  eligible: boolean;
}

export interface TfsaSuccess {
  year: number;
  age: number;
  birthYear: number;
  eligibilityStartYear: number;
  priorContributions: number;
  cumulativeRoom: number;
  remainingRoom: number;
  yearlyBreakdown: TfsaYearlyBreakdown[];
  eligible: true;
}

export interface TfsaError {
  error: string;
  eligible?: false;
}

export type TfsaResult = TfsaSuccess | TfsaError;

export interface CompoundInput {
  principal: number;
  monthlyContribution: number;
  annualRate: number;
  years: number;
  compoundingFrequency?: CompoundingFrequency;
}

export interface CompoundYearlyBreakdown {
  year: number;
  startBalance: number;
  endBalance: number;
  contributionsThisYear: number;
  interestThisYear: number;
  totalContributions: number;
  totalInterest: number;
}

export interface CompoundResult {
  principal: number;
  monthlyContribution: number;
  annualRate: number;
  years: number;
  compoundingFrequency: CompoundingFrequency;
  finalBalance: number;
  totalContributions: number;
  totalInterest: number;
  yearlyBreakdown: CompoundYearlyBreakdown[];
}

export interface TaxInput {
  province: string;
  employmentIncome: number;
  otherIncome?: number;
  rrspDeductions?: number;
  capitalGains?: number;
  taxYear?: number;
}

export interface TaxSuccess {
  province: string;
  taxYear: number;
  employmentIncome: number;
  otherIncome: number;
  rrspDeductions: number;
  capitalGains: number;
  grossIncome: number;
  taxableIncome: number;
  federalTax: number;
  provincialTax: number;
  totalTax: number;
  marginalRate: number;
  averageRate: number;
  afterTaxIncome: number;
}

export interface TaxError {
  error: string;
}

export type TaxResult = TaxSuccess | TaxError;

export function isTfsaError(result: TfsaResult): result is TfsaError {
  return 'error' in result;
}

export function isTaxError(result: TaxResult): result is TaxError {
  return 'error' in result;
}

/* ------------------------------------------------------------------ */
/* VDOT running calculator                                            */
/* ------------------------------------------------------------------ */

/** Race distances supported for PB entry and equivalent-time prediction. */
export type RaceDistance = '1500m' | '1mile' | '3K' | '5K' | '10K' | 'half' | 'marathon';

/** Population cohort for percentile comparison. */
export type RunnerCohort = 'overall' | 'men' | 'women';

/**
 * Which percentile baseline to compare against.
 *  - 'majors': 2024 World Marathon Majors finishers, age × sex graded. Faster,
 *    more trained field than the general population. Only marathon distance.
 *  - 'all': RunRepeat all-runners distributions (5K/10K/half/marathon), no
 *    age grading. The broader recreational population.
 */
export type PercentileSource = 'majors' | 'all';

/** Age-group bucket used by the World Majors dataset (string keys). */
export type AgeGroup =
  | 'Under 20' | '20-24' | '25-29' | '30-34' | '35-39' | '40-44'
  | '45-49' | '50-54' | '55-59' | '60-64' | '65-69' | '70-74'
  | '75-79' | '80 and Over';

/** A race result used to compute VDOT. */
export interface RaceResult {
  distance: RaceDistance;
  /** Finish time in seconds. */
  timeSeconds: number;
}

/** A single predicted equivalent performance. */
export interface EquivalentPerformance {
  distance: RaceDistance;
  label: string;
  /** Predicted time in seconds. */
  timeSeconds: number;
}

/** Training paces, all in seconds per kilometre. */
export interface TrainingPaces {
  /** Easy range — slower bound (s/km). */
  easySlow: number;
  /** Easy range — faster bound (s/km). */
  easyFast: number;
  /** Marathon pace (s/km). */
  marathon: number;
  /** Threshold pace (s/km). */
  threshold: number;
  /** Interval pace (s/km). */
  interval: number;
  /** Repetition pace (s/km). */
  repetition: number;
}

/** Successful VDOT calculation result. */
export interface VdotSuccess {
  vdot: number;
  input: RaceResult;
  equivalents: EquivalentPerformance[];
  paces: TrainingPaces;
}

export interface VdotError {
  error: string;
}

export type VdotResult = VdotSuccess | VdotError;

export function isVdotError(result: VdotResult): result is VdotError {
  return 'error' in result;
}

/** A single percentile cutoff in a distribution: {percentile, finish time}. */
export interface PercentilePoint {
  /** Percentile rank, 0–100 (e.g. 50 = median). */
  percentile: number;
  /** Finish time in seconds at that percentile. */
  timeSeconds: number;
}

/** Percentile result for the runner's PB in a given distance/cohort. */
export interface PercentileResult {
  distance: RaceDistance;
  cohort: RunnerCohort;
  /** Which baseline population this percentile is against. */
  source: PercentileSource;
  /** Age group used (only meaningful when source === 'majors'). */
  ageGroup?: AgeGroup;
  /** Finisher count in the cohort this percentile was drawn from (majors only). */
  cohortSize?: number;
  /** Input PB in seconds. */
  pbSeconds: number;
  /** Estimated percentile rank (0–100). Higher = faster (beats that % of runners). */
  percentile: number;
  /** The distribution used, sorted slowest→fastest by time. */
  distribution: PercentilePoint[];
  /** Human-readable summary, e.g. "Faster than 73% of male marathoners." */
  summary: string;
}

/** One row of the multi-distance percentile comparison. */
export interface PercentileComparisonRow {
  distance: RaceDistance;
  /** Finish time in seconds (the actual PB for the input distance, or a
   * VDOT-projected time for the other distances). */
  timeSeconds: number;
  /** True when `timeSeconds` is projected from VDOT rather than an actual PB. */
  projected: boolean;
  /** Estimated percentile rank (0–100, higher = faster). */
  percentile: number | null;
  /** Human-readable summary for this row. */
  summary: string;
}

/* ------------------------------------------------------------------ */
/* Baby cost calculator                                               */
/* ------------------------------------------------------------------ */

/** Time period over which a recurring rate is expressed. */
export type BabyRatePeriod = 'day' | 'week' | 'month';

/** Category of baby cost item. Drives the accrual model. */
export type BabyItemKind = 'consumable' | 'service' | 'onetime';

/** A single line item in the baby cost calculator. */
export interface BabyItem {
  /** Stable id (preset keys are stable strings; custom items get uuid-ish ids). */
  id: string;
  /** Display label, e.g. "Diapers". */
  label: string;
  /** Accrual model: consumable/service accrue over a range; onetime lands once. */
  kind: BabyItemKind;
  /** True when the item contributes to the total (UI toggle). */
  enabled: boolean;
  /** Cost per unit (consumable), per month (service), or total (onetime). CAD. */
  unitCost: number;
  /**
   * Usage rate: units per `ratePeriod` for consumables. Ignored for services
   * (treated as 1/month) and onetime (treated as 1).
   */
  rate: number;
  /** Period the rate is expressed over. Ignored for service/onetime. */
  ratePeriod: BabyRatePeriod;
  /**
   * Start month (inclusive), relative to birth. For onetime, the month the
   * cost lands. Clamped to the calculation window at compute time.
   */
  startMonth: number;
  /** End month (exclusive). -1 / Infinity means "until end of window". */
  endMonth: number;
  /** True if user-added (vs preset). Presets have a `source` citation. */
  custom: boolean;
  /** Citation for the default values (preset items only). */
  source?: string;
  /** Short explanatory tooltip for preset items (shown as an ⓘ hover/tap). */
  help?: string;
  /**
   * Longer formatted details shown in an expandable accordion row under the item
   * (for content too long for a tooltip, e.g. the daycare Canada breakdown).
   * Newlines separate paragraphs; lines starting with "•" render as bullets.
   */
  details?: string;
}

/** A computed cost series for one item, sampled monthly across the window. */
export interface BabyItemSeries {
  id: string;
  label: string;
  /** Cumulative cost at each month boundary (length = windowMonths + 1). */
  cumulative: number[];
  /** Cost accrued in each month (period cost, not cumulative). */
  perMonth: number[];
}

/** Full calculation result. */
export interface BabyCalcResult {
  /** Number of months in the window (endMonth - startMonth). */
  windowMonths: number;
  /** Month labels for the x-axis: [start, start+1, ... , end]. */
  monthLabels: number[];
  /** Per-item cumulative series (enabled items only). */
  series: BabyItemSeries[];
  /** Elementwise sum of all enabled item series — the stacked total. */
  total: number[];
  /** Grand total over the whole window (last value of `total`). */
  grandTotal: number;
}

/** Input bundle for the calculator (window + items). */
export interface BabyCalcInput {
  /** Window start month (inclusive), relative to birth. Usually 0. */
  windowStartMonth: number;
  /** Window end month (exclusive). E.g. 24 = first 2 years. */
  windowEndMonth: number;
  /** Items to include (enabled ones contribute to the total). */
  items: BabyItem[];
}

